import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { adminBatchUpsertServiceLanes } from '../../api/endpoints/admin';
import { useMutationHook } from '../../hooks/useMutationHook';
import { useTruckCatalog } from '../../hooks/useTruckCatalog';
import { INDIAN_STATES } from '../../config/indianStates';
import { SearchableSelect, type SearchableOption } from '../ui/SearchableSelect';
import { MultiSearchableSelect } from '../ui/MultiSearchableSelect';
import styles from './AddServiceLaneModal.module.css';
import { DialogShell } from '../ui/DialogShell';
import { useDialogDismiss, useIsDirty } from '../ui/dialogDismiss';
import formStyles from './FormModal.module.css';
import { DialogHelp } from '../../help/DialogHelp';

/** Pre-built options for the searchable select — sorted alphabetically with capital subtitles. */
const STATE_OPTIONS: SearchableOption[] = INDIAN_STATES.map((s) => ({
  id: s.id,
  label: s.name,
  subtitle: s.capital,
}));

interface Props {
  fleetManagerId: number;
  onClose: () => void;
  onSaved?: () => void;
}

export function AddServiceLaneModal({ fleetManagerId, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const [fromState, setFromState] = useState('');
  const [toStates, setToStates] = useState<string[]>([]);
  const [selectedTrucks, setSelectedTrucks] = useState<string[]>([]);
  // Catalog types only, never "Other" (#249 Contract section 5).
  const truckCatalog = useTruckCatalog();
  const [error, setError] = useState<string | null>(null);

  const saveMut = useMutationHook(
    () =>
      adminBatchUpsertServiceLanes(fleetManagerId, {
        fromState,
        toStates,
        truckTypes: selectedTrucks,
      }),
    {
      successMessage: `Configured ${toStates.length} service lane${toStates.length > 1 ? 's' : ''} successfully.`,
      invalidateKeys: [
        ['admin', 'users', 'service-lanes', fleetManagerId],
        ['admin', 'service-lanes'],
      ],
      onSuccess: () => {
        onSaved?.();
        onClose();
      },
    },
  );

  const toggleTruck = (id: string) => {
    setSelectedTrucks((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id],
    );
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!fromState) {
      setError('Please select an origin state.');
      return;
    }
    if (toStates.length === 0) {
      setError('Please select at least one destination state.');
      return;
    }
    if (selectedTrucks.length === 0) {
      setError('Please select at least one truck type.');
      return;
    }
    setError(null);
    saveMut.mutate(undefined);
  };

  const fromStateObj = INDIAN_STATES.find((s) => s.id === fromState);

  const dirty = useIsDirty({ fromState, toStates, selectedTrucks });
  const dismiss = useDialogDismiss({ dirty, busy: saveMut.isLoading, onClose });

  return (
    <DialogShell dismiss={dismiss} label={t('domain.addServiceLaneModal.title')} scrimClassName={styles.scrim} panelClassName={styles.panel}>
      <div className={formStyles.titleRow}>
        <h2 className={styles.title}>{t('domain.addServiceLaneModal.title')}</h2>
        <DialogHelp context="service-lane" title={t('domain.addServiceLaneModal.title')} />
      </div>

      <form onSubmit={submit}>
        <label className={styles.label}>{t('domain.addServiceLaneModal.originLabel')}</label>
        <SearchableSelect
          options={STATE_OPTIONS}
          value={fromState}
          onChange={setFromState}
          placeholder={t('domain.addServiceLaneModal.originPlaceholder')}
        />

        <label className={styles.label} style={{ marginTop: 14 }}>
          {t('domain.addServiceLaneModal.destinationLabel')}
        </label>
        <MultiSearchableSelect
          options={STATE_OPTIONS.filter((s) => s.id !== fromState)}
          selectedValues={toStates}
          onChange={setToStates}
          placeholder={t('domain.addServiceLaneModal.destinationPlaceholder')}
        />

        {fromStateObj && toStates.length > 0 && (
          <div style={{ marginTop: 8, fontSize: 12, color: 'var(--accent)', fontWeight: 600 }}>
            {toStates.length > 1
              ? t('domain.addServiceLaneModal.willCreateMany', { count: toStates.length, from: fromStateObj.name })
              : t('domain.addServiceLaneModal.willCreateOne', { count: toStates.length, from: fromStateObj.name })}
          </div>
        )}

        <label className={styles.label} style={{ marginTop: 14 }}>{t('domain.addServiceLaneModal.truckTypesLabel')}</label>
        <div className={styles.checkboxGrid}>
          {truckCatalog.trucks.map((truck) => (
            <label key={truck.id} className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={selectedTrucks.includes(truck.id)}
                onChange={() => toggleTruck(truck.id)}
              />
              <span className={styles.checkboxText}>{truck.name}</span>
            </label>
          ))}
        </div>

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.cancel}
            onClick={dismiss.requestClose}
            disabled={saveMut.isLoading}
          >
            {t('common.actions.cancel')}
          </button>
          <button
            type="submit"
            className={styles.submit}
            disabled={saveMut.isLoading}
          >
            {saveMut.isLoading
              ? 'Adding...'
              : `Add ${toStates.length > 1 ? `${toStates.length} Lanes` : 'Lane'}`}
          </button>
        </div>
      </form>
    </DialogShell>
  );
}
